/**
 * Every global overlay store's "close whatever is open", in one call.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: this was a literal list of three
 * `getState()` calls inside `AuthRouteOverlayReset`, and the problem with a
 * literal list is not that it was wrong -- it names every overlay store that
 * exists today -- but that nothing made it stay right. The next store to acquire
 * a persistent open state would carry it onto `/auth` or `/auth/link`, which is
 * exactly the symptom that component was written for, and nothing anywhere would
 * fail. The list lives here so `transientOverlays.test.ts` can hold it to the
 * store directory: adding a store with an open state and not registering it is a
 * red test rather than a silent regression.
 *
 * "Transient" is the criterion, and it draws a real line rather than meaning
 * "all of them". An overlay belongs here when it describes something the visitor
 * was doing on the PREVIOUS screen. It does not belong here when it describes
 * the state of the app itself, which an auth route does not change -- see the
 * exemptions at the bottom of the test, which are asserted rather than assumed.
 *
 * AIDEV-NOTE [MEETBALL overlay intents]: `lib/overlay/intents.ts`'s posted queue is deliberately
 * NOT registered here (and is not a store the exhaustiveness test below scans either). An intent
 * is posted so the auth routes carry it FORWARD onto the screen the user is about to land on --
 * the inverse of this file's criterion, which is state describing the PREVIOUS screen. Closing it
 * on the auth-route reset would delete the post-auth celebration/join/create-event intent before
 * its own surface ever mounts to read and ack it.
 */
import { useActivationStore } from './activationStore';
import { useOnboardingStore } from './onboardingStore';
import { useShareSheetStore } from './shareSheetStore';

/**
 * The registered stores' close actions, keyed by module name so the
 * exhaustiveness test can compare against the directory listing.
 */
export const TRANSIENT_OVERLAY_CLOSERS: Readonly<Record<string, () => void>> = {
  activationStore: () => {
    // closeAll, not close: a celebration queued behind a held drawer must not land on the door.
    useActivationStore.getState().closeAll();
  },
  onboardingStore: () => {
    useOnboardingStore.getState().closeOnboarding();
  },
  shareSheetStore: () => {
    // Unconditional: closeShareSheet also cancels a DELAYED open that has not
    // fired yet, which would otherwise pop the sheet onto the door a beat later.
    useShareSheetStore.getState().closeShareSheet();
  },
};

/** Closes every registered global overlay. Idempotent. */
export const closeTransientOverlays = (): void => {
  for (const close of Object.values(TRANSIENT_OVERLAY_CLOSERS)) {
    close();
  }
};
